import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../utils/logout";
import { jwtDecode } from "jwt-decode";
import { Icon } from "@iconify/react";
import MessageCounter from "./MessageCounter";
import { ApprovalContext } from "../context/ApprovalContext";
import { getStoredSuperAdminTarget, getEffectiveRole, exitSuperAdminView } from "../utils/superAdminView";

const NavigationMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [openNestedSubmenu, setOpenNestedSubmenu] = useState(null);

  // Consume from centralized context
  const { counts, fetchApprovalCounts, loading, error } = useContext(ApprovalContext);

  // Handle screen resize
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsMenuOpen(false);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Fetch counts when component mounts or when location changes (throttled by context)
  useEffect(() => {
    fetchApprovalCounts();
  }, [fetchApprovalCounts, location.pathname]);

  // Handle Mobile Click Outside
  useEffect(() => {
    if (isMobile && isMenuOpen) {
      const handleClickOutside = (event) => {
        const menu = document.querySelector('.navigation-menu');
        const btn = document.querySelector('.mobile-menu-button');
        if (menu && !menu.contains(event.target) && !btn.contains(event.target)) {
          setIsMenuOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMobile, isMenuOpen]);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token) return null;

  let decodedToken;
  try {
    decodedToken = jwtDecode(token);
  } catch (err) {
    logout();
    return null;
  }

  const actualUserRole = decodedToken.role?.toLowerCase() || "";
  const viewTarget = actualUserRole === "superadmin" ? getStoredSuperAdminTarget() : null;
  const isViewingAsUser = actualUserRole === "superadmin" && !!viewTarget?.role;
  const userRole = getEffectiveRole(actualUserRole);
  const userName = isViewingAsUser ? (viewTarget.name || viewTarget.email || "Selected User") : (decodedToken.name || decodedToken.email);

  // Escalation menu items (common for multiple roles)
  const escalationMenuItems = {
    // Full access roles (can create complaints)
    fullAccess: [
      { 
        text: "Escalation Management", 
        icon: "heroicons-outline:exclamation-triangle",
        children: [
          { path: "/escalation/dashboard", icon: "heroicons-outline:chart-bar", text: "Dashboard" },
          { path: "/escalation/complaints", icon: "heroicons-outline:list-bullet", text: "All Complaints" },
          { path: "/escalation/complaints/new", icon: "heroicons-outline:plus-circle", text: "New Complaint" }
        ]
      }
    ],
    // Read-only access (can view and update, but not create)
    readOnly: [
      { 
        text: "Escalation Management", 
        icon: "heroicons-outline:exclamation-triangle",
        children: [
          { path: "/escalation/dashboard", icon: "heroicons-outline:chart-bar", text: "Dashboard" },
          { path: "/escalation/complaints", icon: "heroicons-outline:list-bullet", text: "All Complaints" }
        ]
      }
    ],
    // Limited access (only assigned complaints)
    limited: [
      { 
        text: "Escalation Management", 
        icon: "heroicons-outline:exclamation-triangle",
        children: [
          { path: "/escalation/complaints", icon: "heroicons-outline:list-bullet", text: "My Assigned Complaints" }
        ]
      }
    ]
  };

  const roleBasedItems = {
    admin: [
      ...escalationMenuItems.fullAccess,
      {
        text: "Account Manager", icon: "heroicons-outline:briefcase",
        children: [
          {
            text: "User Management", icon: "heroicons-outline:users",
            children: [
              { path: "/add-user", icon: "heroicons-outline:user-plus", text: "User Registration Management" },
              { path: "/manage-user", icon: "heroicons-outline:adjustments-horizontal", text: "Manage User Role" }
            ]
          },
          {
            text: "Dealer Management", icon: "heroicons-outline:building-storefront",
            children: [
              { path: "/add-dealer", icon: "heroicons-outline:user-plus", text: "Add Dealer" },
              { path: "/dealer-list", icon: "heroicons-outline:building-office", text: "Dealer List" }
            ]
          },
          {
            text: "Doctor Management", icon: "heroicons-outline:user-group",
            children: [
              { path: "/add-doctor", icon: "heroicons-outline:user-plus", text: "Add Doctor" },
          { path: "/add-hospital", icon: "heroicons-outline:building-office", text: "Add Hospital" },
              { path: "/doctor-list", icon: "heroicons-outline:user-group", text: "Doctor List" }
            ]
          },
          {
            text: "Employee Management", icon: "heroicons-outline:users",
            children: [
              { path: "/add-cm", icon: "heroicons-outline:user-plus", text: "Add CM" },
              { path: "/cm-list", icon: "heroicons-outline:user-group", text: "CM List" },
              { path: "/add-rcm", icon: "heroicons-outline:user-plus", text: "Add RCM" },
              { path: "/rcm-list", icon: "heroicons-outline:user-group", text: "RCM List" },
              { path: "/add-sm", icon: "heroicons-outline:user-plus", text: "Add SM" },
              { path: "/sm-list", icon: "heroicons-outline:user-group", text: "SM List" },
              { path: "/add-rsm", icon: "heroicons-outline:user-plus", text: "Add RSM" },
              { path: "/rsm-list", icon: "heroicons-outline:user-group", text: "RSM List" }
            ]
          },
          { path: "/patient-assignment", icon: "heroicons-outline:arrow-path", text: "Patient Reassignment" },
          { path: "/bulkupload", icon: "heroicons-outline:arrow-up-tray", text: "Excel Upload" }
        ]
      },
      {
        text: "User Dashboard", icon: "heroicons-outline:squares-2x2",
        children: [
          { path: "/admin-dashboard", icon: "heroicons-outline:shield-check", text: "Admin Dashboard" },
          { path: "/hod-dashboard", icon: "heroicons-outline:user-group", text: "HOD Dashboard" },
          { path: "/dealer-dashboard", icon: "heroicons-outline:building-storefront", text: "Dealer Dashboard" },
          { path: "/doctor-dashboard", icon: "heroicons-outline:user-circle", text: "Doctor Dashboard" },
          { path: "/patient-dashboard", icon: "heroicons-outline:user", text: "Patient Dashboard" },
          { path: "/cm-dashboard", icon: "heroicons-outline:users", text: "Clinical Manager Dashboard" },
          { path: "/rcm-dashboard", icon: "heroicons-outline:clipboard-document-check", text: "RCM Dashboard" },
          { path: "/ncm-dashboard", icon: "heroicons-outline:chart-bar", text: "NCM Dashboard" },
          { path: "/rsm-dashboard", icon: "heroicons-outline:chart-pie", text: "RSM Dashboard" },
          { path: "/customercare-dashboard", icon: "heroicons-outline:heart", text: "Customer Care Dashboard" },
          { path: "/opening-closing-report", icon: "heroicons-outline:calculator", text: "Opening/Closing Balance" }
        ]
      },
      {
        text: "Communications", icon: "heroicons-outline:megaphone",
        children: [
          { path: "/new-broadcast", icon: "heroicons-outline:paper-airplane", text: "New Broadcast" },
          { path: "/broadcast-message", icon: "heroicons-outline:clock", text: "Broadcast History" },
          { path: "/email-marketing", icon: "heroicons-outline:mail", text: "Email Marketing" },
          { path: "/email-reports", icon: "heroicons-outline:chart-bar", text: "Campaign Report" }
        ]
      },
      {
        text: "Patient Updates", icon: "heroicons-outline:heart",
        children: [
          { path: "/catheter-insertion", icon: "heroicons-outline:document-check", text: "Catheter Insertion" },
          { path: "/current-statusupdate", icon: "heroicons-outline:pencil-square", text: "Current Status Update" },
          { path: "/transferset-date-update", icon: "heroicons-outline:calendar", text: "Transferset Date Update" }
        ]
      },
      {
        text: "Reports", icon: "heroicons-outline:document-chart-bar",
        children: [
          {
            text: "Clinical Reports", icon: "heroicons-outline:document-text",
            children: [
              { path: "/pet-report", icon: "heroicons-outline:beaker", text: "PET Report" },
              { path: "/adequacy-report", icon: "heroicons-outline:clipboard-document-check", text: "Adequacy Report" },
              { path: "/pet-dashboard", icon: "heroicons-outline:chart-bar", text: "PET Dashboard" },
              { path: "/adequacy-dashboard", icon: "heroicons-outline:chart-pie", text: "Adequacy Dashboard" },
              { path: "/downloadapi-excel", icon: "heroicons-outline:arrow-down-tray", text: "Download Excel API" }
            ]
          },
          { path: "/therapy-change-report", icon: "heroicons-outline:document-chart-bar", text: "Therapy Change Report" },
          { path: "/report", icon: "heroicons-outline:document-chart-bar", text: "Patient Data Excel Report" },
          { path: "/infection-report", icon: "heroicons-outline:shield-exclamation", text: "Infection Report" },
          { path: "/survival-outcome", icon: "heroicons-outline:heart", text: "Survival Outcome" },
          { path: "/focused-report-ncm", icon: "heroicons-outline:document-magnifying-glass", text: "Focused Report" },
          { path: "/rcm-calender", icon: "heroicons-outline:calendar-days", text: "RCM Approval Calendar" },
          { path: "/dar-clinical", icon: "heroicons-outline:document-check", text: "DAR Clinical" },
          { path: "/dar-rcm", icon: "heroicons-outline:document-check", text: "DAR RCM" }
        ]
      },
      // ======================================================
      // Operational menus grouped to reduce Super Admin clutter
      // ======================================================
      {
        text: "Patient Operations", icon: "heroicons-outline:clipboard-document-list",
        children: [
          { path: "/patient-list", icon: "heroicons-outline:users", text: "Patient Management" },
          { path: "/view-patient-details", icon: "heroicons-outline:eye", text: "View Patient Details" },
          { path: "/baseline-form", icon: "heroicons-outline:document-check", text: "Baseline Form" }
        ]
      },
      {
        text: "Orders & Inventory", icon: "heroicons-outline:shopping-cart",
        children: [
          { path: "/all-orders", icon: "heroicons-outline:clipboard-document-list", text: "All Orders" },
          { path: "/govt-listorder", icon: "heroicons-outline:document-text", text: "Govt Order Lists" },
          { path: "/bag-consumptionlist", icon: "heroicons-outline:archive-box", text: "Bag Consumption List" }
        ]
      },
      {
        text: "Data Management", icon: "heroicons-outline:circle-stack",
        children: [
          { path: "/excel-uploader", icon: "heroicons-outline:document-check", text: "Customer Uploader" },
          { path: "/cm-bulkupdate", icon: "heroicons-outline:pencil", text: "CM Bulk Update" },
          { path: "/patientfield-bulkupdate", icon: "heroicons-outline:pencil-square", text: "Patient Field Bulk Updater" }
        ]
      },
      {
        text: "System Management", icon: "heroicons-outline:cog-6-tooth",
        children: [
          { path: "/activity-logs", icon: "heroicons-outline:clock", text: "User Data Activity Logs" }
        ]
      },
    ],
    hod: [
      // Escalation Management - Read Only Access
      ...escalationMenuItems.readOnly,
      
      { path: "/opening-closing-report", icon: "heroicons-outline:calculator", text: "Opening/Closing Balance" },
      { path: "/view-patient-details", icon: "heroicons-outline:eye", text: "View Patient Details" },
      { path: "/survival-outcome", icon: "heroicons-outline:heart", text: "Survival Outcome" },
      { path: "/all-orders", icon: "heroicons-outline:clipboard-document-list", text: "All Orders" },
      { path: "/report", icon: "heroicons-outline:document-chart-bar", text: "Patient Data Excel Report" },
      { path: "/infection-report", icon: "heroicons-outline:shield-exclamation", text: "Infection Report" },
      { path: "/pet-dashboard", icon: "heroicons-outline:chart-bar", text: "PET Dashboard" },
      { path: "/adequacy-dashboard", icon: "heroicons-outline:chart-pie", text: "Adequacy Dashboard" },
      { path: "/update-status", icon: "heroicons-outline:pencil-square", text: "Update Status" },
      { path: "/downloadapi-excel", icon: "heroicons-outline:arrow-down-tray", text: "Download Excel API" },
      { path: "/therapy-change-report", icon: "heroicons-outline:document-chart-bar", text: "Therapy Change Report" },
    ],
    pdcare: [
      // Escalation Management - Full Access
      ...escalationMenuItems.fullAccess,
      
      { path: "/dar-clinical", icon: "heroicons-outline:pencil-square", text: "DAR Report" },
      { path: "/pdcare-approvals", icon: "heroicons-outline:clock", text: "Pending Approvals", badgeCount: counts.pdCareApprovalCount },
      { path: "/dealer-list", icon: "heroicons-outline:building-office", text: "Dealer List" },
      {
        text: "Doctor Management", icon: "heroicons-outline:user-md",
        children: [
          { path: "/add-doctor", icon: "heroicons-outline:user-plus", text: "Add Doctor" },
          { path: "/add-hospital", icon: "heroicons-outline:building-office", text: "Add Hospital" }, 
          { path: "/doctor-list", icon: "heroicons-outline:user-group", text: "Doctor List" }
        ]
      },
      { path: "/infection-report", icon: "heroicons-outline:shield-exclamation", text: "Infection Report" },
      { path: "/opening-closing-report", icon: "heroicons-outline:calculator", text: "Opening/Closing Balance" },
      { path: "/survival-outcome", icon: "heroicons-outline:heart", text: "Survival Outcome" },
      { path: "/transferset-date-update", icon: "heroicons-outline:calendar", text: "Transferset Date Update" },
      { path: "/downloadapi-excel", icon: "heroicons-outline:arrow-down-tray", text: "Download Excel API" },
      { path: "/clinical-reports", icon: "heroicons-outline:document-text", text: "Clinical Reports" },
      { path: "/activity-logs", icon: "heroicons-outline:clock", text: "User Data Activity Logs" },
      { path: "/add-user", icon: "heroicons-outline:user-plus", text: "Register Campaign User" },
      { path: "/email-reports", icon: "heroicons-outline:chart-bar", text: "Campaign Report" },
      { path: "/therapy-change-report", icon: "heroicons-outline:document-chart-bar", text: "Therapy Change Report" },
    ],
    dealer: [
      { path: "/dealer-orderlist", icon: "heroicons-outline:list-bullet", text: "Order List" },
      { path: "/patient-repeatorders", icon: "heroicons-outline:chart-bar", text: "Order Insights" },
      { path: "/broadcast-message", icon: "heroicons-outline:chat-bubble-left-ellipsis", text: "Messages", counter: <MessageCounter /> },
    ],
    patient: [
      { path: "/create-order", icon: "heroicons-outline:shopping-cart", text: "New Order" },
      { path: "/patient-orderlist", icon: "heroicons-outline:list-bullet", text: "My Orders" },
      { path: "/broadcast-message", icon: "heroicons-outline:chat-bubble-left-ellipsis", text: "Messages", counter: <MessageCounter /> },
      { path: "/your-profile", icon: "heroicons-outline:user-circle", text: "Profile" },
    ],
    cm: [
      // Escalation Management - Limited Access (only assigned complaints)
      ...escalationMenuItems.limited,
      
      //{ path: "/counselling-form", icon: "heroicons-outline:document-text", text: "New Patient Registration" },
      { path: "/catheter-insertion", icon: "heroicons-outline:document-check", text: "Catheter Insertion" },
      //{ path: "/therapy-initiation", icon: "heroicons-outline:document-check", text: "Therapy Initiation" },
      //{ path: "/therapy-end", icon: "heroicons-outline:document-check", text: "Therapy End" },
      { path: "/current-statusupdate", icon: "heroicons-outline:pencil-square", text: "CurrentStatusupdate" },
      //{ path: "/update-status", icon: "heroicons-outline:pencil-square", text: "Update Status" },
      { path: "/bag-consumptionform", icon: "heroicons-outline:pencil-square", text: "BagConsumptionForm" },
      { path: "/bag-consumptionlist", icon: "heroicons-outline:pencil-square", text: "BagConsumptionList" },
      { path: "/infection-report", icon: "heroicons-outline:shield-exclamation", text: "Infection Report" },
      { path: "/transferset-date-update", icon: "heroicons-outline:shield-exclamation", text: "TransfersetDate Update" },
      { path: "/opening-closing-report", icon: "heroicons-outline:calculator", text: "Opening/Closing Balance" },
      { path: "/survival-outcome", icon: "heroicons-outline:calculator", text: "Survival Outcome" },
      { path: "/pet-report", icon: "heroicons-outline:pencil-square", text: "PET Report" },
      { path: "/pet-dashboard", icon: "heroicons-outline:pencil-square", text: "PET Dashboard" },
      { path: "/adequacy-report", icon: "heroicons-outline:pencil-square", text: "Adequacy Report" },
      { path: "/adequacy-dashboard", icon: "heroicons-outline:pencil-square", text: "Adequacy Dashboard" },
    ],
    ncm: [
      // Escalation Management - Read Only Access
      ...escalationMenuItems.readOnly,
      
      { path: "/dar-clinical", icon: "heroicons-outline:pencil-square", text: "DAR Report" },
      { path: "/focused-report-ncm", icon: "heroicons-outline:document-magnifying-glass", text: "Focused Report" },
      { path: "/view-patient-details", icon: "heroicons-outline:eye", text: "View Patient Details" },
      { path: "/pet-report", icon: "heroicons-outline:beaker", text: "PET Report" },
      { path: "/pet-dashboard", icon: "heroicons-outline:chart-bar", text: "PET Dashboard" },
      { path: "/adequacy-report", icon: "heroicons-outline:clipboard-document-check", text: "Adequacy Report" },
      { path: "/adequacy-dashboard", icon: "heroicons-outline:chart-pie", text: "Adequacy Dashboard" },
      { path: "/new-broadcast", icon: "heroicons-outline:megaphone", text: "New Broadcast" },
      { path: "/broadcast-message", icon: "heroicons-outline:envelope", text: "Broadcast History" },
      { path: "/opening-closing-report", icon: "heroicons-outline:calculator", text: "Opening/Closing Balance" },
      { path: "/survival-outcome", icon: "heroicons-outline:heart", text: "Survival Outcome" },
      { path: "/infection-report", icon: "heroicons-outline:shield-exclamation", text: "Infection Report" },
      { path: "/transferset-date-update", icon: "heroicons-outline:calendar", text: "Transferset Date Update" },
      { path: "/patient-assignment", icon: "heroicons-outline:arrow-path", text: "Patient ReAssignment" },
      { path: "/clinical-reports", icon: "heroicons-outline:document-text", text: "Clinical Reports" },
      { path: "/activity-logs", icon: "heroicons-outline:clock", text: "User Data Activity Logs" },
      { path: "/therapy-change-report", icon: "heroicons-outline:document-chart-bar", text: "Therapy Change Report" },
    ],
    rcm: [
      // Escalation Management - Read Only Access
      ...escalationMenuItems.readOnly,
      
      //{ path: "/counselling-form", icon: "heroicons-outline:document-text", text: "New Patient Registration" },
      { path: "/catheter-insertion", icon: "heroicons-outline:document-check", text: "Catheter Insertion" },
      //{ path: "/therapy-initiation", icon: "heroicons-outline:document-check", text: "Therapy Initiation" },
     
      { path: "/current-statusupdate", icon: "heroicons-outline:pencil-square", text: "CurrentStatusupdate" },
     
      { path: "/therapy-change-report", icon: "heroicons-outline:document-chart-bar", text: "Therapy Change Report" },
      { path: "/your-migrated-patients", icon: "heroicons-outline:arrow-right-circle", text: "My Migrated Patients" },
      { path: "/transferset-date-update", icon: "heroicons-outline:shield-exclamation", text: "TransfersetDate Update" },
      { path: "/pet-report", icon: "heroicons-outline:beaker", text: "PET Report" },
      { path: "/pet-dashboard", icon: "heroicons-outline:chart-bar", text: "PET Dashboard" },
      { path: "/adequacy-report", icon: "heroicons-outline:clipboard-document-check", text: "Adequacy Report" },
      { path: "/adequacy-dashboard", icon: "heroicons-outline:chart-pie", text: "Adequacy Dashboard" },
      { path: "/rcm-approvals", icon: "heroicons-outline:document-check", text: "New Approvals", badgeCount: counts.rcmNewApprovalCount },
      { path: "/rcm-approve-infection", icon: "heroicons-outline:shield-exclamation", text: "Infection Approvals", badgeCount: counts.rcmInfectionApprovalCount },
      { path: "/rcm-migration-list", icon: "heroicons-outline:arrow-right-circle", text: "Migration Approvals", badgeCount: counts.rcmMigrationApprovalCount },
      //{ path: "/update-status", icon: "heroicons-outline:pencil-square", text: "Update Status" },
      { path: "/infection-report", icon: "heroicons-outline:shield-exclamation", text: "Infection Report" },
      { path: "/opening-closing-report", icon: "heroicons-outline:calculator", text: "Opening/Closing Balance" },
      { path: "/survival-outcome", icon: "heroicons-outline:heart", text: "Survival Outcome" },
      { path: "/clinical-reports", icon: "heroicons-outline:document-text", text: "Clinical Reports" },
      { path: "/dar-rcm", icon: "heroicons-outline:document-check", text: "DAR" },
      { path: "/dar-clinical", icon: "heroicons-outline:pencil-square", text: "DAR Report" },
      { path: "/bag-consumptionlist", icon: "heroicons-outline:pencil-square", text: "BagConsumptionList" },
      //{ path: "/edit-patient", icon: "heroicons-outline:pencil", text: "Edit Patient" },
    ],
    rsm: [
      // Escalation Management - Limited Access
      ...escalationMenuItems.limited,
      
      { path: "/opening-closing-report", icon: "heroicons-outline:calculator", text: "Opening/Closing Balance" },
      { path: "/survival-outcome", icon: "heroicons-outline:heart", text: "Survival Outcome" },
      { path: "/all-orders", icon: "heroicons-outline:clipboard-document-list", text: "All Orders" },
      { path: "/rsm-list", icon: "heroicons-outline:users", text: "RSM List" },
      { path: "/govt-listorder", icon: "heroicons-outline:document-text", text: "Govt Order Lists" },
    ],
    doctor: [
      { path: "/my-patient", icon: "heroicons-outline:user-circle", text: "My Patients" },
      { path: "/pet-dashboard", icon: "heroicons-outline:chart-bar", text: "PET Dashboard" },
      { path: "/adequacy-dashboard", icon: "heroicons-outline:chart-pie", text: "Adequacy Dashboard" },
    ],
    campaignadmin: [
      { path: "/email-reports", icon: "heroicons-outline:chart-bar", text: "Campaign Report" },
      { path: "/excel-uploader", icon: "heroicons-outline:document-check", text: "Customer Uploader" },
    ],
    campaignreport: [
      { path: "/email-reports", icon: "heroicons-outline:chart-bar", text: "Campaign Report" },
    ],
  };

  // Super Admin receives the same navigation capabilities as Admin, while retaining its own role identity.
  roleBasedItems.superadmin = roleBasedItems.admin;

  const dashboardPathByRole = {
    admin: "/admin-dashboard",
    superadmin: "/admin-dashboard",
    hod: "/hod-dashboard",
    dealer: "/dealer-dashboard",
    patient: "/patient-dashboard",
    cm: "/cm-dashboard",
    rcm: "/rcm-dashboard",
    ncm: "/ncm-dashboard",
    rsm: "/rsm-dashboard",
    pdcare: "/customercare-dashboard",
    doctor: "/doctor-dashboard",
    campaignadmin: "/email-marketing",
    campaignreport: "/email-reports"
  };

  const superAdminDashboardSelectorPath = {
    "/admin-dashboard": "/superadmin-dashboard/admin",
    "/hod-dashboard": "/superadmin-dashboard/hod",
    "/dealer-dashboard": "/superadmin-dashboard/dealer",
    "/doctor-dashboard": "/superadmin-dashboard/doctor",
    "/patient-dashboard": "/superadmin-dashboard/patient",
    "/cm-dashboard": "/superadmin-dashboard/cm",
    "/rcm-dashboard": "/superadmin-dashboard/rcm",
    "/ncm-dashboard": "/superadmin-dashboard/ncm",
    "/rsm-dashboard": "/superadmin-dashboard/rsm",
    "/customercare-dashboard": "/superadmin-dashboard/pdcare"
  };

  const getNavigationPath = (path) =>
    actualUserRole === "superadmin" && !isViewingAsUser && superAdminDashboardSelectorPath[path]
      ? superAdminDashboardSelectorPath[path]
      : path;

  const navItems = [
    { path: dashboardPathByRole[userRole] || "/admin-dashboard", icon: "heroicons-outline:home", text: "Dashboard", exact: true },
    ...(roleBasedItems[userRole] || [])
  ];

  return (
    <>
      {isMobile && (
        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="mobile-menu-button fixed z-50 top-4 left-4 p-3 rounded-full bg-blue-600 text-white shadow-lg">
          <Icon icon={isMenuOpen ? "heroicons-outline:x-mark" : "heroicons-outline:bars-3"} className="w-6 h-6" />
        </button>
      )}
      
      <div className={`navigation-menu bg-gray-800 text-white fixed md:sticky top-0 w-64 z-40 transition-all duration-300 ${isMobile ? (isMenuOpen ? "left-0" : "-left-64") : "left-0"} h-screen flex flex-col`}>
        <div className="p-4 bg-gray-900 border-b border-gray-700">
          {isViewingAsUser && (
            <div className="mb-3 rounded-lg border border-blue-400/30 bg-blue-500/10 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-blue-300 font-semibold">Super Admin View</div>
                  <div className="text-sm font-bold text-white truncate">Viewing as {viewTarget.name || "User"}</div>
                  <div className="text-xs text-gray-300 capitalize">{viewTarget.role} • Full Access</div>
                </div>
                <button
                  type="button"
                  onClick={() => { exitSuperAdminView(); navigate("/admin-dashboard", { replace: true }); }}
                  className="shrink-0 rounded-md bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 text-xs font-semibold text-white"
                >
                  Exit
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => { navigate("/change-password"); if (isMobile) setIsMenuOpen(false); }}
              title="Change Password"
              aria-label="Change Password"
              className="group flex-1 bg-gray-700 hover:bg-blue-600 text-white px-3 py-2.5 rounded-md flex items-center justify-center transition-all duration-200 shadow-sm"
            >
              <Icon icon="heroicons-outline:key" className="w-5 h-5" />
              <span className="ml-2 text-sm font-medium">Change Password</span>
            </button>
            <button
              onClick={logout}
              title="Logout"
              aria-label="Logout"
              className="bg-red-600 hover:bg-red-700 text-white p-2.5 rounded-md flex items-center justify-center transition-all duration-200 shadow-sm"
            >
              <Icon icon="heroicons-outline:arrow-left-on-rectangle" className="w-5 h-5" />
            </button>
          </div>
          
          {loading && <div className="text-blue-400 text-xs mb-1 animate-pulse">Syncing counts...</div>}
          {error && <div className="text-red-400 text-xs mb-1">{error}</div>}

          <div className="mt-2">
            <p className="text-gray-300 text-sm">Welcome</p>
            <p className="font-bold text-white truncate">{userName}</p>
            <p className="text-xs text-gray-400 capitalize">{userRole}</p>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <nav className="p-4">
            <ul className="space-y-1">
              {navItems.map((item, index) => {
                const isActive = location.pathname === getNavigationPath(item.path) || (item.exact ? false : location.pathname.startsWith(getNavigationPath(item.path)));
                return (
                  <li key={index}>
                    {item.children ? (
                      <>
                        <button 
                          onClick={() => setOpenSubmenu(openSubmenu === item.text ? null : item.text)} 
                          className={`w-full text-left p-3 rounded-lg flex items-center justify-between hover:bg-gray-700 transition-colors ${openSubmenu === item.text ? 'bg-gray-700' : ''}`}
                        >
                          <div className="flex items-center">
                            <Icon icon={item.icon} className="w-5 h-5 mr-3" />
                            <span>{item.text}</span>
                          </div>
                          <Icon icon={openSubmenu === item.text ? "heroicons-outline:chevron-up" : "heroicons-outline:chevron-down"} className="w-4 h-4" />
                        </button>
                        <ul className={`ml-6 mt-1 space-y-1 overflow-hidden transition-all duration-300 ${openSubmenu === item.text ? "max-h-96" : "max-h-0"}`}>
                          {item.children.map((child, idx) => (
                            <li key={idx}>
                              {child.children ? (
                                <>
                                  <button
                                    onClick={() => setOpenNestedSubmenu(openNestedSubmenu === child.text ? null : child.text)}
                                    className={`w-full text-left p-2 rounded-lg text-sm text-gray-300 hover:bg-gray-700 flex items-center justify-between gap-2 transition-colors ${openNestedSubmenu === child.text ? "bg-gray-700 text-white" : ""}`}
                                  >
                                    <span className="flex items-center gap-2">
                                      {child.icon && <Icon icon={child.icon} className="w-4 h-4" />}
                                      <span>{child.text}</span>
                                    </span>
                                    <Icon icon={openNestedSubmenu === child.text ? "heroicons-outline:chevron-up" : "heroicons-outline:chevron-down"} className="w-3.5 h-3.5" />
                                  </button>
                                  <ul className={`ml-4 mt-1 space-y-1 overflow-hidden transition-all duration-300 ${openNestedSubmenu === child.text ? "max-h-80" : "max-h-0"}`}>
                                    {child.children.map((nested, nestedIdx) => (
                                      <li key={nestedIdx}>
                                        <button
                                          onClick={() => { navigate(getNavigationPath(nested.path)); if(isMobile) setIsMenuOpen(false); }}
                                          className={`w-full text-left p-2 rounded-lg text-xs text-gray-400 hover:bg-gray-700 hover:text-white flex items-center gap-2 transition-colors ${location.pathname === getNavigationPath(nested.path) ? "text-white bg-blue-600" : ""}`}
                                        >
                                          {nested.icon && <Icon icon={nested.icon} className="w-3.5 h-3.5" />}
                                          <span>{nested.text}</span>
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              ) : (
                                <button
                                  onClick={() => { navigate(getNavigationPath(child.path)); if(isMobile) setIsMenuOpen(false); }}
                                  className={`w-full text-left p-2 rounded-lg text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2 transition-colors ${location.pathname === getNavigationPath(child.path) ? "text-white bg-blue-600" : ""}`}
                                >
                                  {child.icon && <Icon icon={child.icon} className="w-4 h-4" />}
                                  <span>{child.text}</span>
                                  {child.badgeCount > 0 && (
                                    <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                      {child.badgeCount}
                                    </span>
                                  )}
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <button 
                        onClick={() => { navigate(getNavigationPath(item.path)); if(isMobile) setIsMenuOpen(false); }} 
                        className={`w-full text-left p-3 rounded-lg flex items-center justify-between transition-colors ${isActive ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'}`}
                      >
                        <div className="flex items-center">
                          <Icon icon={item.icon} className="w-5 h-5 mr-3" />
                          <span>{item.text}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {item.badgeCount > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                              {item.badgeCount}
                            </span>
                          )}
                          {item.counter}
                        </div>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
      {isMobile && isMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-30 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />}
    </>
  );
};

export default NavigationMenu;